import type { OrderStatus, Role } from '@runnerboy/types';

/** Who may request a transition. SYSTEM = server-internal (payment webhook, timers, dispatch engine). */
export type TransitionActor = 'CUSTOMER' | 'STORE' | 'RUNNER' | 'OPS' | 'SYSTEM';

interface TransitionDef {
  to: OrderStatus;
  actors: TransitionActor[];
}

const t = (to: OrderStatus, ...actors: TransitionActor[]): TransitionDef => ({ to, actors });

/**
 * The complete set of permitted order transitions. Anything not listed is rejected.
 * CART / CHECKOUT_STARTED / COMPLIANCE_CHECK are recorded in history during checkout; an Order row is
 * persisted only once compliance passes (at PAYMENT_PENDING).
 */
export const ORDER_TRANSITIONS: Readonly<Record<OrderStatus, readonly TransitionDef[]>> = {
  CART: [t('CHECKOUT_STARTED', 'CUSTOMER')],
  CHECKOUT_STARTED: [t('COMPLIANCE_CHECK', 'SYSTEM'), t('FAILED', 'SYSTEM')],
  COMPLIANCE_CHECK: [t('PAYMENT_PENDING', 'SYSTEM'), t('FAILED', 'SYSTEM')],
  PAYMENT_PENDING: [t('PLACED', 'SYSTEM'), t('FAILED', 'SYSTEM'), t('CANCELLED', 'CUSTOMER', 'OPS', 'SYSTEM')],
  PLACED: [t('STORE_CONFIRMATION_PENDING', 'SYSTEM'), t('CANCELLED', 'CUSTOMER', 'OPS', 'SYSTEM')],
  STORE_CONFIRMATION_PENDING: [
    t('STORE_ACCEPTED', 'STORE', 'OPS'),
    t('STORE_REJECTED', 'STORE', 'OPS', 'SYSTEM'),
    t('CANCELLED', 'CUSTOMER', 'OPS', 'SYSTEM'),
  ],
  STORE_ACCEPTED: [t('RUNNER_SEARCHING', 'SYSTEM', 'OPS'), t('CANCELLED', 'CUSTOMER', 'OPS', 'SYSTEM')],
  STORE_REJECTED: [t('REFUND_PENDING', 'SYSTEM'), t('CANCELLED', 'SYSTEM')],
  RUNNER_SEARCHING: [t('RUNNER_ASSIGNED', 'SYSTEM', 'OPS'), t('CANCELLED', 'CUSTOMER', 'OPS', 'SYSTEM')],
  RUNNER_ASSIGNED: [
    t('RUNNER_TO_STORE', 'RUNNER', 'SYSTEM'),
    t('RUNNER_SEARCHING', 'SYSTEM', 'OPS'),
    t('CANCELLED', 'CUSTOMER', 'OPS', 'SYSTEM'),
  ],
  RUNNER_TO_STORE: [
    t('RUNNER_AT_STORE', 'RUNNER', 'OPS'),
    t('RUNNER_SEARCHING', 'SYSTEM', 'OPS'),
    t('CANCELLED', 'CUSTOMER', 'OPS', 'SYSTEM'),
  ],
  RUNNER_AT_STORE: [
    t('ORDER_PREPARING', 'SYSTEM', 'STORE'),
    t('READY_FOR_PICKUP', 'SYSTEM', 'STORE'),
    t('RUNNER_SEARCHING', 'OPS', 'SYSTEM'),
    t('CANCELLED', 'OPS', 'SYSTEM'),
  ],
  ORDER_PREPARING: [t('READY_FOR_PICKUP', 'STORE', 'SYSTEM'), t('RUNNER_SEARCHING', 'OPS', 'SYSTEM'), t('CANCELLED', 'OPS', 'SYSTEM')],
  READY_FOR_PICKUP: [t('PICKED_UP', 'RUNNER', 'STORE', 'OPS'), t('RUNNER_SEARCHING', 'OPS', 'SYSTEM'), t('CANCELLED', 'OPS', 'SYSTEM')],
  PICKED_UP: [
    t('OUT_FOR_DELIVERY', 'RUNNER', 'SYSTEM'),
    t('AGE_VERIFICATION_PENDING', 'SYSTEM'),
    t('RETURN_TO_STORE', 'OPS', 'SYSTEM'),
  ],
  AGE_VERIFICATION_PENDING: [t('OUT_FOR_DELIVERY', 'SYSTEM', 'OPS'), t('RETURN_TO_STORE', 'OPS', 'SYSTEM')],
  OUT_FOR_DELIVERY: [
    t('RUNNER_NEARBY', 'SYSTEM', 'RUNNER'),
    t('ARRIVED', 'RUNNER'),
    t('RETURN_TO_STORE', 'OPS', 'SYSTEM'),
  ],
  RUNNER_NEARBY: [t('ARRIVED', 'RUNNER'), t('RETURN_TO_STORE', 'OPS', 'SYSTEM')],
  ARRIVED: [t('HANDOVER_VERIFICATION', 'RUNNER'), t('DELIVERY_REFUSED', 'RUNNER', 'OPS'), t('RETURN_TO_STORE', 'OPS', 'SYSTEM')],
  HANDOVER_VERIFICATION: [t('DELIVERED', 'RUNNER'), t('DELIVERY_REFUSED', 'RUNNER', 'OPS')],
  DELIVERED: [t('REFUND_PENDING', 'OPS', 'SYSTEM')],
  DELIVERY_REFUSED: [t('RETURN_TO_STORE', 'SYSTEM', 'RUNNER', 'OPS')],
  RETURN_TO_STORE: [t('RETURNED', 'STORE', 'OPS')],
  RETURNED: [t('REFUND_PENDING', 'SYSTEM', 'OPS'), t('CANCELLED', 'SYSTEM', 'OPS')],
  CANCELLED: [t('REFUND_PENDING', 'SYSTEM', 'OPS')],
  REFUND_PENDING: [t('REFUNDED', 'SYSTEM', 'OPS')],
  REFUNDED: [],
  FAILED: [],
};

/** No further transitions are possible. */
export const TERMINAL_STATUSES: readonly OrderStatus[] = ['REFUNDED', 'FAILED'];

/** Statuses after which the order is finished from the customer's point of view (no more tracking). */
export const CLOSED_STATUSES: readonly OrderStatus[] = [
  'DELIVERED',
  'CANCELLED',
  'FAILED',
  'REFUND_PENDING',
  'REFUNDED',
  'STORE_REJECTED',
  'RETURNED',
];

/** Orders in these statuses hold reserved (not yet sold) inventory. */
export const PRE_PICKUP_STATUSES: readonly OrderStatus[] = [
  'PAYMENT_PENDING',
  'PLACED',
  'STORE_CONFIRMATION_PENDING',
  'STORE_ACCEPTED',
  'RUNNER_SEARCHING',
  'RUNNER_ASSIGNED',
  'RUNNER_TO_STORE',
  'RUNNER_AT_STORE',
  'ORDER_PREPARING',
  'READY_FOR_PICKUP',
];

/** Goods are physically with the runner. */
export const IN_TRANSIT_STATUSES: readonly OrderStatus[] = [
  'PICKED_UP',
  'AGE_VERIFICATION_PENDING',
  'OUT_FOR_DELIVERY',
  'RUNNER_NEARBY',
  'ARRIVED',
  'HANDOVER_VERIFICATION',
  'DELIVERY_REFUSED',
  'RETURN_TO_STORE',
];

/** Statuses during which a runner is attached to the order. */
export const RUNNER_ACTIVE_STATUSES: readonly OrderStatus[] = [
  'RUNNER_ASSIGNED',
  'RUNNER_TO_STORE',
  'RUNNER_AT_STORE',
  'ORDER_PREPARING',
  'READY_FOR_PICKUP',
  ...IN_TRANSIT_STATUSES,
];

/**
 * Runner GPS is visible to the customer only while the runner is actually travelling for that order.
 * Never after completion/cancellation and never during the return-to-store leg.
 */
export const CUSTOMER_LOCATION_VISIBLE_STATUSES: readonly OrderStatus[] = [
  'RUNNER_ASSIGNED',
  'RUNNER_TO_STORE',
  'RUNNER_AT_STORE',
  'ORDER_PREPARING',
  'READY_FOR_PICKUP',
  'PICKED_UP',
  'AGE_VERIFICATION_PENDING',
  'OUT_FOR_DELIVERY',
  'RUNNER_NEARBY',
  'ARRIVED',
  'HANDOVER_VERIFICATION',
];

export class InvalidTransitionError extends Error {
  constructor(
    readonly from: OrderStatus,
    readonly to: OrderStatus,
    readonly actor: TransitionActor,
  ) {
    super(`Order transition ${from} -> ${to} is not permitted for ${actor}`);
    this.name = 'InvalidTransitionError';
  }
}

export function canTransition(from: OrderStatus, to: OrderStatus, actor: TransitionActor): boolean {
  return ORDER_TRANSITIONS[from].some((d) => d.to === to && d.actors.includes(actor));
}

export function assertTransition(from: OrderStatus, to: OrderStatus, actor: TransitionActor): void {
  if (!canTransition(from, to, actor)) throw new InvalidTransitionError(from, to, actor);
}

export function nextStatuses(from: OrderStatus, actor?: TransitionActor): OrderStatus[] {
  return ORDER_TRANSITIONS[from].filter((d) => !actor || d.actors.includes(actor)).map((d) => d.to);
}

export function isTerminal(status: OrderStatus): boolean {
  return ORDER_TRANSITIONS[status].length === 0;
}

export function isRunnerLocationVisibleToCustomer(status: OrderStatus): boolean {
  return CUSTOMER_LOCATION_VISIBLE_STATUSES.includes(status);
}

export function actorForRoles(roles: readonly Role[]): TransitionActor {
  if (roles.some((r) => ['CITY_OPERATIONS', 'CUSTOMER_SUPPORT', 'ADMIN', 'SUPER_ADMIN'].includes(r))) return 'OPS';
  if (roles.includes('RUNNER')) return 'RUNNER';
  if (roles.some((r) => ['STORE_STAFF', 'STORE_MANAGER', 'STORE_OWNER'].includes(r))) return 'STORE';
  return 'CUSTOMER';
}

/** Customer-facing progress steps (for the tracking timeline). */
export const CUSTOMER_PROGRESS_STEPS: { key: string; statuses: OrderStatus[] }[] = [
  { key: 'placed', statuses: ['PLACED', 'STORE_CONFIRMATION_PENDING'] },
  { key: 'accepted', statuses: ['STORE_ACCEPTED', 'RUNNER_SEARCHING'] },
  { key: 'runnerAssigned', statuses: ['RUNNER_ASSIGNED', 'RUNNER_TO_STORE'] },
  { key: 'atStore', statuses: ['RUNNER_AT_STORE', 'ORDER_PREPARING', 'READY_FOR_PICKUP'] },
  { key: 'onTheWay', statuses: ['PICKED_UP', 'AGE_VERIFICATION_PENDING', 'OUT_FOR_DELIVERY', 'RUNNER_NEARBY'] },
  { key: 'arrived', statuses: ['ARRIVED', 'HANDOVER_VERIFICATION'] },
  { key: 'delivered', statuses: ['DELIVERED'] },
];

export function progressIndex(status: OrderStatus): number {
  return CUSTOMER_PROGRESS_STEPS.findIndex((s) => s.statuses.includes(status));
}
